import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const CookieConsentContext = createContext();

const CONSENT_KEY = 'cookieConsent';
const CONSENT_VERSION = 1;
const CONSENT_EXPIRY_MONTHS = 6;

// Default consent state
const defaultConsent = {
  essential: true,  // Always enabled
  analytics: false, // Google Analytics 4
  marketing: false, // Future use
  timestamp: null,
  version: CONSENT_VERSION,
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Check if consent exists and is valid
  const hasValidConsent = useCallback(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return false;

      const parsed = JSON.parse(stored);
      
      // Check if consent has expired (6 months)
      if (parsed.timestamp) {
        const expiryDate = new Date(parsed.timestamp);
        expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);
        
        if (new Date() > expiryDate) {
          localStorage.removeItem(CONSENT_KEY);
          return false;
        }
      }

      // Check if version matches
      if (parsed.version !== CONSENT_VERSION) {
        localStorage.removeItem(CONSENT_KEY);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  }, []);

  // Load consent from localStorage
  const loadConsent = useCallback(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConsent(parsed);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading consent:', error);
      return false;
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = useCallback((consentData) => {
    try {
      const dataToSave = {
        ...consentData,
        timestamp: Date.now(),
        version: CONSENT_VERSION,
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(dataToSave));
      setConsent(dataToSave);
      return true;
    } catch (error) {
      console.error('Error saving consent:', error);
      return false;
    }
  }, []);

  // Give consent to specific categories
  const giveConsent = useCallback((categories) => {
    const newConsent = {
      ...defaultConsent,
      ...categories,
      essential: true, // Always true
    };
    
    if (saveConsent(newConsent)) {
      setShowBanner(false);
      setShowSettings(false);
      
      // Trigger analytics initialization if analytics consent given
      if (newConsent.analytics && window.initializeGA4) {
        window.initializeGA4();
      }
      
      return true;
    }
    return false;
  }, [saveConsent]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    return giveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  }, [giveConsent]);

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    return giveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  }, [giveConsent]);

  // Update specific consent category
  const updateConsent = useCallback((category, value) => {
    if (category === 'essential') {
      // Cannot disable essential cookies
      return false;
    }

    const newConsent = {
      ...consent,
      [category]: value,
    };

    if (saveConsent(newConsent)) {
      // If analytics disabled, disable GA4
      if (category === 'analytics' && !value && window.disableGA4) {
        window.disableGA4();
      }
      
      // If analytics enabled, initialize GA4
      if (category === 'analytics' && value && window.initializeGA4) {
        window.initializeGA4();
      }
      
      return true;
    }
    return false;
  }, [consent, saveConsent]);

  // Revoke specific consent category
  const revokeConsent = useCallback((category) => {
    return updateConsent(category, false);
  }, [updateConsent]);

  // Reset all consent
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(defaultConsent);
    setShowBanner(true);
    
    // Disable GA4 if it was enabled
    if (window.disableGA4) {
      window.disableGA4();
    }
    
    return true;
  }, []);

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  // Close settings modal
  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (hasValidConsent()) {
      loadConsent();
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, [hasValidConsent, loadConsent]);

  const value = {
    consent,
    showBanner,
    showSettings,
    hasValidConsent,
    giveConsent,
    acceptAll,
    rejectAll,
    updateConsent,
    revokeConsent,
    resetConsent,
    openSettings,
    closeSettings,
    setShowBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Custom hook to use cookie consent
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

export default CookieConsentContext;
